import React, { useState } from "react";
import {
  FiAlertTriangle,
  FiEdit2,
  FiFolder,
  FiFolderMinus,
  FiPause,
  FiPlay,
  FiPlus,
  FiTrash2,
  FiZap,
} from "react-icons/fi";
import { useAgendamento } from "../../context/AgendamentoContext";
import { formatarFrequencia } from "../../utils/agendamento/calendar";
import { obterLabel } from "../../utils/painelRegistry";
import { Button, Message } from "../../pages/Acessos/styles";
import { Panel, PanelTitle } from "../../pages/Configuracoes/styles";
import ModalNovoAgendamento from "./ModalNovoAgendamento";
import HistoricoExecucoes from "./HistoricoExecucoes";
import {
  AgendamentosLista,
  Card,
  CardActions,
  CardBtn,
  CardChips,
  CardHeader,
  CardMeta,
  CardTitulo,
  Chip,
  EmptyState,
  PastaInfo,
  PastaNome,
  PausarRow,
  SecaoHeader,
  StatusPill,
  ToggleSwitch,
  EntradaOculta,
} from "./styles";

// `pausarTodos` entra na conta: com o global ligado, o motor devolve [] logo
// na primeira linha de decidirDisparos, mas os cards continuavam verdes
// dizendo "ATIVO · Próxima: 22/08 08:00". Duas verdades na mesma tela.
function statusBadgeTone(ag, permissao, pausarTodos) {
  if (pausarTodos || !ag.ativo) return "warning";
  if (permissao !== "granted") return "warning";
  return "success";
}

function statusBadgeLabel(ag, permissao, pausarTodos) {
  if (pausarTodos) return "Pausado (global)";
  if (!ag.ativo) return "Pausado";
  if (permissao !== "granted") return "Aguardando permissão";
  return "Ativo";
}

function formatarProximaExec(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function nomesPaineis(paineis = []) {
  return paineis.map((p) => obterLabel(p.painelId)).join(", ");
}

export default function PainelAgendamentos() {
  const {
    suportado,
    config,
    executando,
    nomeDiretorio,
    permissao,
    escolherPasta,
    removerPasta,
    reativarPermissao,
    criarAgendamento,
    atualizarAgendamento,
    removerAgendamento,
    togglePausarTodos,
    executar,
  } = useAgendamento();

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);

  if (!suportado) {
    return (
      <Panel>
        <PanelTitle>Exportação automática</PanelTitle>
        <Message $tone="warning">
          Esta funcionalidade requer Google Chrome ou Microsoft Edge desktop (versão
          86 ou superior). Outros navegadores não suportam seleção de pasta local
          pelo navegador.
        </Message>
      </Panel>
    );
  }

  const temPasta = Boolean(nomeDiretorio);
  const permissaoOk = permissao === "granted";
  const precisaReativar = temPasta && permissao === "prompt";
  const negada = temPasta && permissao === "denied";

  function abrirNovo() {
    setEditando(null);
    setModalAberto(true);
  }
  function abrirEditar(ag) {
    setEditando(ag);
    setModalAberto(true);
  }
  function fecharModal() {
    setModalAberto(false);
    setEditando(null);
  }
  function salvar(dados) {
    if (editando) {
      atualizarAgendamento(editando.id, dados);
    } else {
      criarAgendamento(dados);
    }
    fecharModal();
  }

  return (
    <Panel>
      <SecaoHeader>
        <PanelTitle>Exportação automática</PanelTitle>
        <Button type="button" onClick={abrirNovo} disabled={!temPasta}>
          <FiPlus size={14} />
          Novo agendamento
        </Button>
      </SecaoHeader>

      <p style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: -6 }}>
        Exporta automaticamente para uma pasta do Windows enquanto o app está aberto.
        Execuções atrasadas (com app fechado) disparam quando você reabre.
        PDF/Excel/CSV usam o snapshot do painel; PNG/JPG capturam a página em um
        iframe oculto.
      </p>

      <PastaInfo>
        <FiFolder size={16} />
        {temPasta ? (
          <PastaNome>
            Pasta: <strong>{nomeDiretorio}</strong>
          </PastaNome>
        ) : (
          <span>Nenhuma pasta selecionada</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Button type="button" $variant="secondary" onClick={escolherPasta}>
            <FiFolder size={14} />
            {temPasta ? "Trocar pasta" : "Selecionar pasta"}
          </Button>
          {temPasta && (
            <Button type="button" $variant="secondary" onClick={removerPasta}>
              <FiFolderMinus size={14} />
              Remover
            </Button>
          )}
        </div>
      </PastaInfo>

      {precisaReativar && (
        <Message $tone="warning">
          <FiAlertTriangle size={14} style={{ marginRight: 6, verticalAlign: "-2px" }} />
          O navegador suspendeu a permissão para escrever na pasta.{" "}
          <button
            type="button"
            onClick={reativarPermissao}
            style={{
              background: "transparent",
              border: "none",
              color: "inherit",
              textDecoration: "underline",
              fontWeight: 700,
              cursor: "pointer",
              padding: 0,
            }}
          >
            Reativar permissão
          </button>
        </Message>
      )}

      {negada && (
        <Message $tone="danger">
          Permissão de gravação negada. Selecione a pasta novamente.
        </Message>
      )}

      {/* Input focável ANTES do switch (o anel de foco usa `+`), e o <label>
          em volta faz o clique — um handler só, e Tab/Espaço funcionam.
          Antes: <input hidden> (fora da ordem de tabulação) + <span onClick>. */}
      {temPasta && (
        <PausarRow>
          <EntradaOculta
            type="checkbox"
            checked={config.pausarTodos}
            onChange={togglePausarTodos}
          />
          <ToggleSwitch $on={config.pausarTodos} />
          <span>Pausar todos os agendamentos globalmente</span>
        </PausarRow>
      )}

      {config.agendamentos.length === 0 && temPasta && (
        <EmptyState>
          Nenhum agendamento configurado. Clique em <strong>"Novo agendamento"</strong>{" "}
          para criar.
        </EmptyState>
      )}

      <AgendamentosLista>
        {config.agendamentos.map((ag) => (
          <Card key={ag.id}>
            <CardHeader>
              <CardTitulo>{ag.nome}</CardTitulo>
              <StatusPill
                $tone={statusBadgeTone(ag, permissao, config.pausarTodos)}
              >
                {statusBadgeLabel(ag, permissao, config.pausarTodos)}
              </StatusPill>
            </CardHeader>
            <CardMeta>
              <span>
                <strong>Painéis:</strong> {nomesPaineis(ag.paineis) || "—"}
              </span>
              <span>
                <strong>Frequência:</strong> {formatarFrequencia(ag.frequencia)}
              </span>
              <span>
                <strong>Próxima:</strong>{" "}
                {config.pausarTodos || !ag.ativo
                  ? "—"
                  : formatarProximaExec(ag.proximaExecucao)}
              </span>
              {ag.subpasta && (
                <span>
                  <strong>Subpasta:</strong> {ag.subpasta}
                </span>
              )}
              {ag.ocultarPrecos && (
                <span style={{ fontStyle: "italic" }}>(preços ocultos)</span>
              )}
            </CardMeta>
            <CardChips>
              {ag.formatos.map((f) => (
                <Chip key={f}>{f.toUpperCase()}</Chip>
              ))}
            </CardChips>
            <CardActions>
              <CardBtn
                onClick={() => atualizarAgendamento(ag.id, { ativo: !ag.ativo })}
              >
                {ag.ativo ? <FiPause size={11} /> : <FiPlay size={11} />}
                {ag.ativo ? "Pausar" : "Ativar"}
              </CardBtn>
              {/* `executando` vinha do contexto e a tela ignorava: um lote de
                  5 painéis x 3 formatos leva minutos com a tela idêntica, e o
                  usuário clicava de novo achando que não pegou. */}
              <CardBtn
                $primary
                onClick={() => executar(ag)}
                disabled={!permissaoOk || executando}
                title={executando ? "Uma exportação já está em andamento" : undefined}
              >
                <FiZap size={11} />
                {executando ? "Exportando…" : "Executar agora"}
              </CardBtn>
              <CardBtn onClick={() => abrirEditar(ag)}>
                <FiEdit2 size={11} />
                Editar
              </CardBtn>
              <CardBtn
                onClick={() => {
                  if (window.confirm(`Remover o agendamento "${ag.nome}"?`)) {
                    removerAgendamento(ag.id);
                  }
                }}
              >
                <FiTrash2 size={11} />
                Remover
              </CardBtn>
            </CardActions>
          </Card>
        ))}
      </AgendamentosLista>

      <HistoricoExecucoes />

      {/* A `key` remonta o modal a cada agendamento: os useState internos são
          inicializados a partir da prop `agendamento` e, sem isto, o componente
          montava UMA vez com null e "Editar" abria o formulário em branco — com
          o botão Salvar desabilitado, porque nome/painéis vinham vazios. */}
      <ModalNovoAgendamento
        key={editando?.id || "novo"}
        aberto={modalAberto}
        agendamento={editando}
        onClose={fecharModal}
        onSave={salvar}
      />
    </Panel>
  );
}
